import { Marker, Popup, Tooltip } from 'react-leaflet';
import { DivIcon } from 'leaflet';
import { Drone, droneLabel } from '../../types/drone';
import { escapeHtml } from '../../utils/escapeHtml';
import { formatBatteryPercentage, getBatteryLevel, getBatteryColor } from '../../utils/battery';
import { useTheme } from '../../hooks/useTheme';
import { useNow } from '../../hooks/useNow';
import { isTelemetryStale } from '../../utils/staleness';
import {
  formatDroneStatus,
  formatHeading,
  formatRelativeTime,
  formatSpeed,
} from '../../utils/formatters';
import { useDispatch } from 'react-redux';
import { selectDrone } from '../../store/slices/dronesSlice';

/**
 * A drone the pilot cannot see any more, drawn at its last position and never
 * faded (#787, #799). Shape and words as well as colour: the badge and the
 * label say which, so colour is not the only signal.
 */
interface MarkerAlarm {
  /** Ring, badge and label colour, whatever the battery. */
  color: string;
  /** The badge on the ring, as HTML. */
  badge: string;
  /** The label line under the name. */
  label: string;
  /** What a screen reader hears, and the tooltip's first words. */
  spoken: string;
  /** Why the last report is all there is. */
  meaning: string;
}

/** A crossed-out network: the link is gone (#799, K2). */
const NETWORK_OFF_ICON = `<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
  <path fill="currentColor" d="M2 4.27 3.28 3 21 20.72 19.73 22l-3.07-3.07A11 11 0 0 1 12 20 11 11 0 0 1 1 9.4l1.6-1.2A9 9 0 0 0 12 18c1.1 0 2.15-.2 3.13-.55l-1.65-1.65A6.9 6.9 0 0 1 12 16a7 7 0 0 1-6.27-3.9l1.62-1.2A5 5 0 0 0 12 14c.25 0 .5-.02.74-.06L2 4.27zM12 4a11 11 0 0 1 11 5.4l-1.6 1.2A9 9 0 0 0 9.4 6.4L7.8 4.8A11 11 0 0 1 12 4z"/>
</svg>`;

const ALARMS: Partial<Record<Drone['status'], MarkerAlarm>> = {
  // Yellow: silent in the air, cause unknown (#787, #799 K2).
  link_lost: {
    color: '#f9a825',
    badge: NETWORK_OFF_ICON,
    label: 'LINK LOST',
    spoken: 'link lost',
    meaning: 'Where it was then, not where it is now.',
  },
  // Red: its controller stopped above the ground; the autopilot's failsafe has
  // it (#799, K4, K10).
  shut_down_in_flight: {
    color: '#d32f2f',
    badge: '!',
    label: 'SHUT DOWN IN FLIGHT',
    spoken: 'shut down in flight',
    meaning: "Our controller stopped in the air; the autopilot's failsafe has it.",
  },
};

interface DroneMarkerProps {
  drone: Drone;
}

export const DroneMarker: React.FC<DroneMarkerProps> = ({ drone }) => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch();
  const batteryLevel = getBatteryLevel(drone.battery.percentage);
  const batteryColor = getBatteryColor(batteryLevel, isDarkMode);

  // Staleness becomes true when nothing happens, so the clock has to tick for
  // it (#577). Without this a drone that stops reporting keeps its last live
  // marker indefinitely — which is exactly how a 61-day silence drew like a
  // drone in flight.
  const stale = isTelemetryStale(drone.lastUpdateTime, useNow());
  const lastSeen = formatRelativeTime(drone.lastUpdateTime);
  // Lost or shut down in the air (#787, #799): drawn at the last position it
  // reported, and not faded like an old reading. Nobody can see this aircraft
  // any more, and its pilot has to find where it was.
  const alarm = ALARMS[drone.status];

  // Create custom marker icon with battery status
  const createDroneIcon = () => {
    const batteryText = formatBatteryPercentage(drone.battery.percentage);
    // A stale drone is drawn, but never as a live one (#577): faded, with a
    // dashed ring instead of a solid one, and its own last-seen line in place
    // of a battery reading it cannot vouch for. Still on the map — an aircraft
    // nobody is watching is the failure this system cares about most, and
    // removing it would be the #508 mistake wearing a new face.
    //
    // Shape and opacity, not colour alone: "this reading is old" is not
    // something to convey by hue, and the ring colour is already spoken for by
    // the battery level.
    const secondLine = alarm
      ? `<span style="color: ${alarm.color}">${alarm.label}</span><br/>last heard ${lastSeen}`
      : stale
        ? `last seen ${lastSeen}`
        : batteryText;
    // Where the nose points (#711): a pointer on the ring, turned to the
    // heading the aircraft reports. Not the direction of travel, which the
    // route line already shows; in a crosswind the two differ.
    //
    // No heading, no pointer. Drawing one at 0° would claim north for an
    // aircraft that has not said where it points.
    const heading = Number.isFinite(drone.heading) ? drone.heading : undefined;
    const headingPointer =
      heading === undefined
        ? ''
        : `<div data-heading="${heading}" style="
            position: absolute;
            inset: -10px;
            transform: rotate(${heading}deg);
            pointer-events: none;
          ">
            <div style="
              margin: 0 auto;
              width: 0;
              height: 0;
              border-left: 6px solid transparent;
              border-right: 6px solid transparent;
              border-bottom: 9px solid ${batteryColor};
            "></div>
          </div>`;
    const headingLabel =
      heading === undefined ? '' : `, heading ${formatHeading(heading)}`;
    // The name a pilot typed, escaped: this string is HTML (see escapeHtml).
    const label = escapeHtml(droneLabel(drone));
    const iconHtml = `
      <div data-stale="${stale}" data-alarm="${alarm ? drone.status : ''}" aria-label="${label}${headingLabel}${
        alarm
          ? `, ${alarm.spoken}, last heard ${lastSeen}`
          : stale
            ? `, telemetry stale, last seen ${lastSeen}`
            : ''
      }" style="
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        opacity: ${stale && !alarm ? '0.45' : '1'};
      ">
        <div style="
          position: relative;
          background: ${isDarkMode ? '#2d2d2d' : '#ffffff'};
          border: ${alarm ? `3px dashed ${alarm.color}` : `2px ${stale ? 'dashed' : 'solid'} ${batteryColor}`};
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          ${headingPointer}
          ${
            alarm
              ? `<div data-alarm-badge style="
                  position: absolute;
                  top: -6px;
                  right: -6px;
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  background: ${alarm.color};
                  color: #ffffff;
                  font-size: 12px;
                  font-weight: bold;
                  line-height: 18px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                ">${alarm.badge}</div>`
              : ''
          }
          🚁
        </div>
        <div style="
          background: ${isDarkMode ? '#2d2d2d' : '#ffffff'};
          color: ${isDarkMode ? '#e0e0e0' : '#212121'};
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
          margin-top: 4px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.2);
          white-space: nowrap;
        ">
          ${label}<br/>
          ${secondLine}
        </div>
      </div>
    `;

    return new DivIcon({
      html: iconHtml,
      className: 'drone-marker',
      iconSize: [60, 80],
      iconAnchor: [30, 40],
    });
  };

  const handleClick = () => {
    dispatch(selectDrone(drone.id));
  };

  // A marker is a position; without one there is nothing to place (#508).
  // FleetClusterLayer already filters these out, so this is the second line of
  // defence rather than the first — but a marker that renders itself at 0,0 when
  // asked to render nowhere is worse than one that declines.
  if (!drone.position) {
    return null;
  }

  return (
    <Marker
      position={[drone.position.latitude, drone.position.longitude]}
      icon={createDroneIcon()}
      eventHandlers={{
        click: handleClick,
      }}
    >
      <Tooltip direction="top" offset={[0, -40]}>
        <div>
          <strong>{droneLabel(drone)}</strong>
          <br />
          {alarm ? (
            <>
              {formatDroneStatus(drone.status)}, last heard {lastSeen}
              <br />
              {alarm.meaning}
            </>
          ) : stale ? (
            <>
              Last seen {lastSeen}
              <br />
              Status and battery below are from that report, not now.
            </>
          ) : (
            <>
              Status: {formatDroneStatus(drone.status)}
              <br />
              Battery: {formatBatteryPercentage(drone.battery.percentage)}
            </>
          )}
        </div>
      </Tooltip>
      <Popup>
        <div>
          <h3 style={{ margin: '0 0 8px 0' }}>{droneLabel(drone)}</h3>
          {(stale || alarm) && (
            <p style={{ margin: '4px 0', fontWeight: 'bold' }}>
              {alarm ? `${formatDroneStatus(drone.status)}, last heard` : 'Last seen'} {lastSeen}.
              Everything below is from that report.
            </p>
          )}
          <p style={{ margin: '4px 0' }}>
            <strong>ID:</strong> {drone.id}
          </p>
          <p style={{ margin: '4px 0' }}>
            <strong>Status:</strong> {formatDroneStatus(drone.status)}
          </p>
          <p style={{ margin: '4px 0' }}>
            <strong>Battery:</strong>{' '}
            {formatBatteryPercentage(drone.battery.percentage)}
          </p>
          <p style={{ margin: '4px 0' }}>
            <strong>Altitude:</strong> {drone.position.altitude.toFixed(1)}m
            {/* Reached only when position exists — see the guard above. */}
          </p>
          {/* Where it pointed and how fast it went (#787, H5): for a lost
              drone, which way to look. */}
          <p style={{ margin: '4px 0' }}>
            <strong>Heading:</strong> {formatHeading(drone.heading)}
          </p>
          <p style={{ margin: '4px 0' }}>
            <strong>Speed:</strong> {formatSpeed(drone.speed)}
          </p>
        </div>
      </Popup>
    </Marker>
  );
};
